import type { ControlPoint, Coordinate, Drawing } from '@/types'
import { pointToLineDistance } from './Canvas.utils'
import { applyChaikin, CHAIKIN_ITERATIONS } from './Chaikin.utils'
import {
	angleFromDeltas,
	distance,
	pointToSegmentInfo,
} from './Geometry.utils'
import {
	getOrderedPointsFromDrawing,
	getSegmentPoints,
} from './DrawingPoint.utils'

export type CoordSystemLike = {
	feetToPixels: (x: number, y: number) => Coordinate
	pixelsToFeet: (x: number, y: number) => Coordinate
	scale: number
}

export type SegmentHitResult = {
	segmentIndex: number
	insertPosition: Coordinate
	distance: number
}

const CHAIKIN_MULTIPLIER = 8

/**
 * Helper: Find the closest point in an array to a target point.
 */
function findClosestPoint(
	points: Coordinate[],
	target: Coordinate,
): { index: number; distance: number } {
	let closestIndex = 0
	let minDist = Infinity

	for (let i = 0; i < points.length; i++) {
		const p = points[i]
		if (!p) continue
		const dist = distance(target, p)
		if (dist < minDist) {
			minDist = dist
			closestIndex = i
		}
	}

	return { index: closestIndex, distance: minDist }
}

/**
 * Helper: Find the closest segment in ordered points to a target point.
 */
function findClosestSegment(
	orderedPoints: ControlPoint[],
	targetPixel: Coordinate,
	coordSystem: { feetToPixels: (x: number, y: number) => Coordinate },
): { index: number; distance: number } {
	let closestSegmentIndex = 0
	let minDist = Infinity

	for (let i = 0; i < orderedPoints.length - 1; i++) {
		const p1Coord = orderedPoints[i]
		const p2Coord = orderedPoints[i + 1]
		if (!p1Coord || !p2Coord) continue

		const p1 = coordSystem.feetToPixels(p1Coord.x, p1Coord.y)
		const p2 = coordSystem.feetToPixels(p2Coord.x, p2Coord.y)

		const { distance } = pointToSegmentInfo(targetPixel, p1, p2)
		if (distance < minDist) {
			minDist = distance
			closestSegmentIndex = i
		}
	}

	return { index: closestSegmentIndex, distance: minDist }
}

/**
 * Helper: Apply Chaikin smoothing to ordered points, returning smoothed pixel coordinates.
 */
function getSmoothedPixelPoints(
	orderedPoints: ControlPoint[],
	coordSystem: {
		feetToPixels: (x: number, y: number) => Coordinate
		scale: number
	},
): Coordinate[] {
	const pixelPoints = orderedPoints.map(p =>
		coordSystem.feetToPixels(p.x, p.y),
	)
	return applyChaikin(pixelPoints, CHAIKIN_ITERATIONS)
}

/**
 * Find closest position on a curved path using Chaikin smoothing.
 */
function findClosestPositionOnCurvedPath(
	drawing: Drawing,
	orderedPoints: ControlPoint[],
	clickPositionFeet: Coordinate,
	coordSystem: CoordSystemLike,
): SegmentHitResult | null {
	// Apply Chaikin smoothing to get curved path
	const pixelPoints = orderedPoints.map(p => coordSystem.feetToPixels(p.x, p.y))
	const smoothedPixels = applyChaikin(pixelPoints, CHAIKIN_ITERATIONS)

	const clickPixel = coordSystem.feetToPixels(clickPositionFeet.x, clickPositionFeet.y)

	// Find closest point on smoothed curve
	let closestSmoothedIndex = 0
	let closestT = 0
	let minDist = Infinity

	for (let i = 0; i < smoothedPixels.length - 1; i++) {
		const p1 = smoothedPixels[i]
		const p2 = smoothedPixels[i + 1]
		if (!p1 || !p2) continue

		const { distance, t } = pointToSegmentInfo(clickPixel, p1, p2)
		if (distance < minDist) {
			minDist = distance
			closestSmoothedIndex = i
			closestT = t
		}
	}

	// Map smoothed index back to original segment
	const originalSegmentIndex = Math.min(
		Math.floor(closestSmoothedIndex / CHAIKIN_MULTIPLIER),
		drawing.segments.length - 1,
	)

	// Calculate insert position by interpolating on the smoothed curve
	const p1 = smoothedPixels[closestSmoothedIndex]
	const p2 = smoothedPixels[closestSmoothedIndex + 1]
	if (!p1 || !p2) return null

	const insertPixel = {
		x: p1.x + closestT * (p2.x - p1.x),
		y: p1.y + closestT * (p2.y - p1.y),
	}
	const insertFeet = coordSystem.pixelsToFeet(insertPixel.x, insertPixel.y)

	return {
		segmentIndex: originalSegmentIndex,
		insertPosition: insertFeet,
		distance: minDist,
	}
}

/**
 * Find closest position on a sharp (non-smoothed) path.
 */
function findClosestPositionOnSharpPath(
	drawing: Drawing,
	clickPositionFeet: Coordinate,
	coordSystem: CoordSystemLike,
): SegmentHitResult | null {
	const clickPixel = coordSystem.feetToPixels(clickPositionFeet.x, clickPositionFeet.y)
	let bestResult: SegmentHitResult | null = null
	let bestDistance = Infinity

	for (let i = 0; i < drawing.segments.length; i++) {
		const segment = drawing.segments[i]
		if (!segment) continue

		const points = segment.pointIds
			.map(id => drawing.points[id])
			.filter(Boolean) as ControlPoint[]

		if (points.length < 2) continue

		const firstPoint = points[0]
		const lastPoint = points[points.length - 1]
		if (!firstPoint || !lastPoint) continue

		const p1Pixel = coordSystem.feetToPixels(firstPoint.x, firstPoint.y)
		const p2Pixel = coordSystem.feetToPixels(lastPoint.x, lastPoint.y)

		const { distance, t } = pointToSegmentInfo(clickPixel, p1Pixel, p2Pixel)

		if (distance < bestDistance) {
			bestDistance = distance
			const insertFeet = {
				x: firstPoint.x + t * (lastPoint.x - firstPoint.x),
				y: firstPoint.y + t * (lastPoint.y - firstPoint.y),
			}
			bestResult = {
				segmentIndex: i,
				insertPosition: insertFeet,
				distance,
			}
		}
	}

	return bestResult
}

/**
 * Find which segment a point is closest to, considering Chaikin smoothing for curved paths.
 * Returns the segment index and the position where a new point should be inserted.
 */
export function findClosestSegmentPosition(
	drawing: Drawing,
	clickPositionFeet: Coordinate,
	coordSystem: CoordSystemLike,
): SegmentHitResult | null {
	if (drawing.segments.length === 0) return null

	const orderedPoints = getOrderedPointsFromDrawing(drawing)
	if (orderedPoints.length < 2) return null

	if (drawing.style.pathMode === 'curve') {
		return findClosestPositionOnCurvedPath(
			drawing,
			orderedPoints,
			clickPositionFeet,
			coordSystem,
		)
	} else {
		return findClosestPositionOnSharpPath(drawing, clickPositionFeet, coordSystem)
	}
}

/**
 * Get tangent angle for curved paths using smoothed points.
 */
function getCurveTangentAtPoint(
	drawing: Drawing,
	targetPoint: Coordinate,
	coordSystem: CoordSystemLike,
	orderedPoints: ControlPoint[],
): number {
	// For curved paths, use smoothed points to get accurate tangent
	const smoothedPixels = getSmoothedPixelPoints(orderedPoints, coordSystem)

	// Find closest point on smoothed curve
	const targetPixel = coordSystem.feetToPixels(
		targetPoint.x,
		targetPoint.y,
	)
	const { index: closestIndex } = findClosestPoint(
		smoothedPixels,
		targetPixel,
	)

	// Get tangent from neighboring points
	const prev = smoothedPixels[Math.max(0, closestIndex - 1)]
	const next = smoothedPixels[Math.min(smoothedPixels.length - 1, closestIndex + 1)]

	if (!prev || !next) return 0

	const dx = next.x - prev.x
	const dy = next.y - prev.y
	return angleFromDeltas(dx, dy)
}

/**
 * Get tangent angle for sharp paths using line segments.
 */
function getSharpTangentAtPoint(
	drawing: Drawing,
	targetPoint: Coordinate,
	coordSystem: CoordSystemLike,
	orderedPoints: ControlPoint[],
): number {
	// For sharp paths, find which line segment the point is on
	const targetPixel = coordSystem.feetToPixels(targetPoint.x, targetPoint.y)
	const { index: closestSegmentIndex } = findClosestSegment(
		orderedPoints,
		targetPixel,
		coordSystem,
	)

	// Calculate angle from segment
	const p1 = orderedPoints[closestSegmentIndex]
	const p2 = orderedPoints[closestSegmentIndex + 1]

	if (!p1 || !p2) return 0

	const dx = p2.x - p1.x
	const dy = p2.y - p1.y
	return angleFromDeltas(dx, dy)
}

/**
 * Get the tangent angle at a specific point on a drawing's path.
 * Returns the angle in radians, calculated from the path direction at that point.
 * For curved paths, uses the smoothed curve. For sharp paths, uses line segments.
 */
export function getPathTangentAtPoint(
	drawing: Drawing,
	targetPoint: Coordinate,
	coordSystem: CoordSystemLike,
): number {
	const orderedPoints = getOrderedPointsFromDrawing(drawing)
	if (orderedPoints.length < 2) return 0

	if (drawing.style.pathMode === 'curve') {
		return getCurveTangentAtPoint(
			drawing,
			targetPoint,
			coordSystem,
			orderedPoints,
		)
	} else {
		return getSharpTangentAtPoint(
			drawing,
			targetPoint,
			coordSystem,
			orderedPoints,
		)
	}
}

/**
 * Build SVG path commands from a range of points.
 */
function buildPathCommands(
	points: Coordinate[],
	startIndex: number,
	endIndex: number,
): string {
	if (startIndex < 0 || endIndex >= points.length || startIndex > endIndex) {
		return ''
	}

	const commands: string[] = []
	const firstPoint = points[startIndex]
	if (firstPoint) {
		commands.push(`M ${firstPoint.x} ${firstPoint.y}`)
		for (let i = startIndex + 1; i <= endIndex; i++) {
			const p = points[i]
			if (p) {
				commands.push(`L ${p.x} ${p.y}`)
			}
		}
	}

	return commands.join(' ')
}

/**
 * Build split paths for smooth (Chaikin-smoothed) paths.
 */
function buildSmoothSplitPaths(
	orderedPoints: ControlPoint[],
	splitPoint: Coordinate,
	coordSystem: CoordSystemLike,
): { preSnapPath: string; postSnapPath: string } {
	const smoothedPixels = getSmoothedPixelPoints(orderedPoints, coordSystem)

	if (smoothedPixels.length < 2) {
		return { preSnapPath: '', postSnapPath: '' }
	}

	// Find closest smoothed point to split point
	const splitPixel = coordSystem.feetToPixels(splitPoint.x, splitPoint.y)
	const { index: splitIndex } = findClosestPoint(smoothedPixels, splitPixel)

	// Build pre-snap path (start to split point)
	const preSnapPath = splitIndex > 0
		? buildPathCommands(smoothedPixels, 0, splitIndex)
		: ''

	// Build post-snap path (split point to end)
	const postSnapPath = splitIndex < smoothedPixels.length - 1
		? buildPathCommands(smoothedPixels, splitIndex, smoothedPixels.length - 1)
		: ''

	return { preSnapPath, postSnapPath }
}

/**
 * Build split paths for sharp (non-smoothed) paths.
 */
function buildSharpSplitPaths(
	orderedPoints: ControlPoint[],
	splitPoint: Coordinate,
	coordSystem: CoordSystemLike,
): { preSnapPath: string; postSnapPath: string } {
	const splitPixel = coordSystem.feetToPixels(splitPoint.x, splitPoint.y)
	const { index: splitSegmentIndex } = findClosestSegment(
		orderedPoints,
		splitPixel,
		coordSystem,
	)

	if (splitSegmentIndex < 0 || orderedPoints.length === 0) {
		return { preSnapPath: '', postSnapPath: '' }
	}

	// Convert ordered points to pixel coordinates
	const pixelPoints = orderedPoints.map(pt =>
		coordSystem.feetToPixels(pt.x, pt.y),
	)

	// Build pre-snap path (start to split point)
	const preSnapPoints = [
		...pixelPoints.slice(0, splitSegmentIndex + 1),
		splitPixel,
	]
	const preSnapPath = buildPathCommands(preSnapPoints, 0, preSnapPoints.length - 1)

	// Build post-snap path (split point to end)
	const postSnapPoints = [splitPixel, ...pixelPoints.slice(splitSegmentIndex + 1)]
	const postSnapPath = buildPathCommands(postSnapPoints, 0, postSnapPoints.length - 1)

	return { preSnapPath, postSnapPath }
}

/**
 * Split a drawing's path into two SVG path strings at a specific point (snap point).
 * Returns paths for pre-snap (dashed) and post-snap (solid) portions for motion type pre-snap movement.
 */
export function splitPathAtPoint(
	drawing: Drawing,
	splitPoint: Coordinate,
	coordSystem: CoordSystemLike,
): { preSnapPath: string; postSnapPath: string } {
	const orderedPoints = getOrderedPointsFromDrawing(drawing)
	if (orderedPoints.length < 2) {
		return { preSnapPath: '', postSnapPath: '' }
	}

	const isAllLineSegments = drawing.segments.every(s => s.type === 'line')
	const shouldSmooth = isAllLineSegments && drawing.style.pathMode === 'curve'

	if (shouldSmooth) {
		return buildSmoothSplitPaths(orderedPoints, splitPoint, coordSystem)
	} else {
		return buildSharpSplitPaths(orderedPoints, splitPoint, coordSystem)
	}
}

/**
 * Check if a drawing should be smoothed with Chaikin algorithm.
 * Smooth drawings are those with all line segments and pathMode === 'curve'.
 */
export function shouldSmoothDrawing(drawing: Drawing): boolean {
	const isAllLineSegments = drawing.segments.every(s => s.type === 'line')
	return isAllLineSegments && drawing.style.pathMode === 'curve'
}

/**
 * Collect all unique control points from a drawing in order,
 * apply a coordinate transform, and return Chaikin-smoothed points.
 * Returns null if drawing should not be smoothed.
 */
export function getSmoothedPoints<T extends Coordinate>(
	drawing: Drawing,
	transform: (point: ControlPoint) => T,
): T[] | null {
	if (!shouldSmoothDrawing(drawing)) {
		return null
	}

	// Collect all unique points in order
	const seenPoints = new Set<string>()
	const allPoints: T[] = []

	for (const segment of drawing.segments) {
		for (const pointId of segment.pointIds) {
			if (!seenPoints.has(pointId)) {
				seenPoints.add(pointId)
				const point = drawing.points[pointId]
				if (point) {
					allPoints.push(transform(point))
				}
			}
		}
	}

	if (allPoints.length < 2) {
		return null
	}

	// Apply Chaikin smoothing
	// Cast is safe because T extends Coordinate and Chaikin produces Coordinate objects
	return applyChaikin(allPoints, CHAIKIN_ITERATIONS) as T[]
}

export function isPointNearDrawing(
	drawing: Drawing,
	coordSystem: { feetToPixels: (x: number, y: number) => Coordinate },
	pixelPoint: Coordinate,
	paddingPx: number,
): boolean {
	const segments = drawing.segments
	for (const segment of segments) {
		const points = getSegmentPoints(drawing, segment)
		for (let i = 0; i < points.length - 1; i++) {
			const p1Feet = points[i]!
			const p2Feet = points[i + 1]!
			const p1 = coordSystem.feetToPixels(p1Feet.x, p1Feet.y)
			const p2 = coordSystem.feetToPixels(p2Feet.x, p2Feet.y)
			const dist = pointToLineDistance(pixelPoint, p1, p2)
			if (dist <= paddingPx) return true
		}
	}
	return false
}

export function isPointNearControlPoint(
	drawing: Drawing,
	coordSystem: { feetToPixels: (x: number, y: number) => Coordinate },
	pixelPoint: Coordinate,
	paddingPx: number,
): boolean {
	// Check all points in the shared pool
	for (const point of Object.values(drawing.points)) {
		const pixel = coordSystem.feetToPixels(point.x, point.y)
		const dist = distance(pixelPoint, pixel)
		if (dist <= paddingPx) return true
	}
	return false
}


